import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, Flame, Beer, Music } from "lucide-react";
import { imgSrc, scrollToSection } from "../../lib/api";

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export function Hero({ settings }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex items-end sm:items-center overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src={imgSrc(settings?.hero_image)}
          alt="Atmosfera serale con cocktail e luci calde"
          className="w-full h-full object-cover brightness-[1.12] saturate-[1.05]"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/35 to-burgundy-deep/20" />
      <div className="absolute inset-0 spotlight" />

      <motion.div style={{ opacity: fade }} className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full pb-28 pt-36 sm:py-40">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.32em] text-gold-glow mb-6"
        >
          Bar • Ristopub • Pizzeria — Ceccano (FR)
        </motion.p>

        <h1 data-testid="hero-headline-text" className="font-serif text-[13vw] sm:text-6xl lg:text-[5.2rem] leading-[1.02] tracking-tight text-cream max-w-4xl">
          <MaskedLine delay={0.35}>Pizza, gusto</MaskedLine>
          <MaskedLine delay={0.5}>
            e <em className="text-gold-glow not-italic font-medium italic">serate</em>
          </MaskedLine>
          <MaskedLine delay={0.65}>
            <em className="italic">da vivere.</em>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.95 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-cream-muted font-light leading-relaxed"
        >
          A Ceccano, il punto d'incontro tra pizza fragrante, piatti da pub fatti bene,
          birre fresche e cocktail miscelati al momento.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-menu-cta-button"
            onClick={() => scrollToSection("#menu")}
            className="bg-burgundy-brand hover:bg-burgundy-glow text-cream text-xs font-bold uppercase tracking-[0.22em] px-8 py-4 rounded-full border border-gold/30 hover:border-gold/70 transition-all duration-300 hover:-translate-y-0.5"
          >
            Scopri il menu
          </button>
          <button
            data-testid="hero-book-cta-button"
            onClick={() => scrollToSection("#prenota")}
            className="border border-cream/30 hover:border-gold-glow text-cream hover:text-gold-glow text-xs font-bold uppercase tracking-[0.22em] px-8 py-4 rounded-full transition-all duration-300 hover:-translate-y-0.5 backdrop-blur-sm"
          >
            Prenota un tavolo
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.35 }}
          className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.2em] text-cream-subtle"
        >
          <span className="inline-flex items-center gap-2"><Flame className="w-3.5 h-3.5 text-copper" /> Forno a vista</span>
          <span className="inline-flex items-center gap-2"><Beer className="w-3.5 h-3.5 text-copper" /> Birre & drink</span>
          <span className="inline-flex items-center gap-2"><Music className="w-3.5 h-3.5 text-copper" /> Serate & eventi</span>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollToSection("#il-locale")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        aria-label="Scorri"
        className="absolute bottom-24 sm:bottom-10 left-1/2 -translate-x-1/2 text-cream-subtle hover:text-gold-glow transition-colors"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }} className="block">
          <ChevronDown className="w-6 h-6" />
        </motion.span>
      </motion.button>
    </section>
  );
}
